
import { Injectable, OnDestroy} from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';

import moment from 'moment-timezone';
import { MessageService } from 'primeng/api';

import { forkJoin, of, Observable, Subscription, BehaviorSubject } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, tap, filter } from 'rxjs/operators';
import { find, union, unionBy, difference, differenceBy } from 'lodash';

import { SavedMentionSearchesApi } from '../../../api/mentions/saved-mention-searches.api';
import type { ReportModel } from '../models/report.model';
import { ReportsService } from './reports.service';
import { MentionSearchModel } from '../../../Monitor/Shared/models/mention-search.model';
import { SavedMentionSearchApiModel } from '../../../api/mentions/model/saved-mention-search.api.model';
import { ReportSeriesModel } from '../models/report-series.model';
import { MentionsSearchResponseModel } from '../../../api/mentions/model/mentions-search-response.model';
import { MentionsApi } from '../../../api/mentions/mentions.api';
import { ReportsSeriesMapperService } from './reports-series-mapper.service';
import { SearchCriteriaModel } from '../../../api/mentions/model/search-criteria.model';
import { MentionSearchFacetTypesEnum } from '../../../api/mentions/enum/mention-search-facet-types.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';
import { mentionSeriesTypes, SeriesTypeEnum } from '../enums/series-type.enum';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { ReportDateRangeModel } from '../models/report-date-range.model';
import { SeriesStateEnum } from '../enums/series-state.enum';
import { ExternalDataApi } from '../../../api/externalData/external-data.api';
import { GoogleAnalyticsReportResponseModel } from '../../../api/externalData/models/google-analytics-report.response';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { DataConnectorModel } from '../../../Core/Users/models/data-connector.model';
import { DataConnectorTypesEnum } from '../../../api/externalData/enums/data-connector-types.enum';
import { GoogleAnalyticsReportFrequencyEnum } from '../../../api/externalData/enums/google-analytics-report-frequency.enum';
import { ReportsExternalDataMapperService } from './report-external-data-mapper.service';
import { DateQueryModel } from '../../Forms/models/date-query.model';
import { IndexeddbCoreCacheService } from '../../../Core/Cache/services/indexeddb-core-cache.service';
import { ProminenceByReachRequestModel } from '../../../api/mentions/model/prominence-by-reach-request.model';
import { DateFacetModel } from '../../../api/mentions/model/date-facet.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { SearchCriteriaDateQueryModel } from '../../../api/mentions/model/search-criteria-date-query.model';
import { MediaSubscriptionService } from '../../../Monitor/Core/services/media-subscription.service';
import { MediaSubscription } from '../../../Monitor/Shared/models/media-subscription.model';
import { ThemeService } from '../../../Social/Intelligence/services/theme.service';
import { Theme } from '../../../Social/Intelligence/models/theme.model';
import { CurrencyConversionService } from '../../../Core/Users/services/currency-conversion.service';
import { NeoFacetRequestApiModel } from '../../../api/mentions/model/neo-facet-request-api.model';
import { MentionSearchQueryModel } from '../../../Monitor/Shared/models/mention-search-query.model';
import { ReportBlockTypeEnum } from '../enums/report-block-type.enum';
import { Mention } from '../../Mentions/models/mention';
import { RelativeTimezoneDateTypeEnum } from '../../../api/mentions/enum/relative-timezone-date-type.enum';
import { DataConnectionService } from '../../../Core/Users/services/data-connection.service';
import { LinkedinSubTypeEnum } from '@Common/Basic/enums/linkedin-sub-type.enum';
import { TwitterSubTypeEnum } from '@Common/Basic/enums/twitter-sub-type.enum';
import { ReportsSeriesDataMapperService } from './reports-series-data-mapper.service';
import { DataPointModel } from '../../../Common/Charts/models/data-point.model';
import { newDataProcessingWorkflowBlockTypes } from '../models/new-data-processing-workflow-block-types.model';
import { ChartDisplayElementModel } from '../models/chart-display-element.model';
import { DataConnectorsApi } from '@api/externalData/data-connectors.api';
import { ReportSeriesSearchResult } from '../models/report-series-search-result';
import { BlueskySubTypeEnum } from '@Common/Basic/enums/bluesky-sub-type.enum';
import { AgentService} from '@Social/Intelligence/services/agent.service';
import { ChartTimeIntervalsEnum } from '../enums/chart-time-intervals.enum';
import { BroadcastSubTypeEnum } from '../../../Common/Basic/enums/broadcast-sub-type.enum';


// When new external data sources are added, add to this type as an OR
type ExternalDataResponseModels = GoogleAnalyticsReportResponseModel;

@Injectable()
export class ReportsSeriesService implements OnDestroy {

    mentionDataProvidersMap: Map<string, BehaviorSubject<MentionsSearchResponseModel>>;
    mentionDataMap: Map<string, MentionsSearchResponseModel>;

    // is the current report in the process of reloading its data as part of a refresh
    isRefresh: boolean = false;

    public googleAnalyticsDataConnectors: DataConnectorModel[];
    public externalDataProvidersMap: Map<string, BehaviorSubject<ExternalDataResponseModels>>;
    public externalDataMap: Map<string, ExternalDataResponseModels | boolean>;

    private globalState: SeriesStateEnum = SeriesStateEnum.none;
    private globalState$: BehaviorSubject<SeriesStateEnum> = new BehaviorSubject<SeriesStateEnum>(SeriesStateEnum.none);

    public showSourceValidateError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public hasBadData: boolean = false;

    protected agents: MediaSubscription[];
    protected socialThemes: Theme[];
    protected userTimezonePreference: string;
    protected subscriptions: Subscription[] = [];

    private currentReport: ReportModel;

    constructor(
        public savedSearchesApi: SavedMentionSearchesApi,
        public mentionsApi: MentionsApi,
        public reportsSeriesMapperService: ReportsSeriesMapperService,
        public localizationService: LocalizationService,
        public reportsService: ReportsService,
        public externalDataMapperService: ReportsExternalDataMapperService,
        public messageService: MessageService,
        public currentUserStateService: CurrentUserStateService,
        public externalDataApi: ExternalDataApi,
        public indexedDbCoreCacheService: IndexeddbCoreCacheService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public themeService: ThemeService,
        public currencyConversionService: CurrencyConversionService,
        public dataConnectionService: DataConnectionService,
        public reportsSeriesDataMapperService: ReportsSeriesDataMapperService,
        public dataConnectorsApiService: DataConnectorsApi,
        public agentService: AgentService
    ) {
        this.mentionDataProvidersMap = new Map<string, BehaviorSubject<MentionsSearchResponseModel>>();
        this.mentionDataMap = new Map<string, MentionsSearchResponseModel>();
        this.externalDataProvidersMap = new Map<string, BehaviorSubject<ExternalDataResponseModels>>();
        this.externalDataMap = new Map<string, ExternalDataResponseModels | boolean>();
        this.setSubscriptions();
    }

    setSubscriptions(): void {
        this.subscriptions.push(
            this.reportsService?.getCurrentReport().subscribe(
                currentReport => {
                    if (!currentReport) {
                        this.clearMapping();
                        this.isRefresh = false;

                        this.checkIfShouldSetGlobalState();

                        return;
                    }
                    if (this.currentReport !== currentReport && this.currentReport?.id !== currentReport.id) {
                        // report changed, clear out old data.
                        this.clearMapping();
                        this.isRefresh = false;
                    }
                    this.currentReport = currentReport;
                    this.prepareReport();
                }
            ),
            this.dataConnectionService?.googleAnalyticsDataConnectors$.pipe(filter(connectors => !!connectors)).subscribe(
                connectors => {
                    this.googleAnalyticsDataConnectors = connectors;

                    if (this.currentReport?.getExternalDataSeries()) {
                        this.prepareExternalSeries();
                    }
                }
            ),
            this.currentUserStateService.getPreferences().pipe(filter(preferences => !!preferences)).subscribe(
                preferences => {
                    this.userTimezonePreference = preferences.timeZone;
                }
            ),
            this.agentService.all$.pipe(filter(x => !!x)).subscribe(
                agents => {
                    this.agents = agents;
                }
            ),
            this.themeService.all$.subscribe((socialThemesList) => {
                this.socialThemes = socialThemesList;
            })
        );
    }

    public prepareReport(): void {
        this.prepareMentionSeries();
        this.prepareExternalSeries();
        this.checkIfShouldSetGlobalState();
    }

    /**
     * Loop through series and compile a list of search criteria (along with matching series)
     * Fetch topic searches if it does not exist for the series
     * Loop through the list to perpare mention searches
     */
    prepareMentionSeries(): void {
        this.mentionDataProvidersMap.clear();
        this.mentionDataMap.clear();
        const searchesMap: Map<SearchCriteriaModel, ReportSeriesModel[]> = new Map();

        // some series (topic and subscriptions right now) save their search object in the b/e
        // we need to retrieve this search object in order to perform a facet search using it.
        const seriesSavedSearchRequestMap: Map<ReportSeriesModel, Observable<SavedMentionSearchApiModel>> = new Map();

        for (const series of this.currentReport.getMentionsSeries()) {
            if (series.mentions && !series.isSharedReport && !series.styles?.ignoreApiCall) {
                let request: Observable<SavedMentionSearchApiModel>;
                // topic search object does not exist - request the search below
                if (series.mentions.topicId && !series.mentions.topic) {
                    request = this.savedSearchesApi.getById(series.mentions.topicId);
                    seriesSavedSearchRequestMap.set(series, request);
                    continue;
                }

                if (series.mentions.mediaSubscriptionId && !series.mentions.mediaSubscription) {
                    const mentionSearchModel = new MentionSearchModel();
                    mentionSearchModel.searchCriteria.query.subscription.include = [series.mentions.mediaSubscriptionId];
                    series.mentions.mediaSubscription = mentionSearchModel;
                }
                this.addToSearchMap(searchesMap, series.toSearchCriteriaModel(), series);
            } else if (series.styles?.ignoreApiCall) {
                const seriesKey = series.getSeriesKey();
                if (!this.mentionDataProvidersMap.get(seriesKey)) {
                    this.mentionDataMap.set(seriesKey, null);
                }
            }
        }

        for (const series of this.currentReport.getSocialSeries()) {
            if (series.mentions && !series.isSharedReport && !series.styles?.ignoreApiCall) {
                if (series.mentions.socialTopicId && !series.mentions.socialTopic) {
                    const mentionSearchModel = new MentionSearchModel();
                    mentionSearchModel.searchCriteria.query.agent.include = [series.mentions.socialTopicId];
                    series.mentions.socialTopic = mentionSearchModel;
                    series.styles.socialTopicThemeIds = find(this.agents, {id: series.mentions.socialTopicId})?.themeIds;
                } else if (series.mentions.themeId && !series.mentions.theme) {
                    const mentionSearchModel = new MentionSearchModel();
                    mentionSearchModel.searchCriteria.query.theme.include = [series.mentions.themeId];
                    series.mentions.theme = mentionSearchModel;
                    series.styles.socialTopicIds = find(this.socialThemes, {id: series.mentions.themeId})?.subscriptionIds;
                }

                this.addToSearchMap(searchesMap, series.toSearchCriteriaModel(), series);
            } else if (series.styles?.ignoreApiCall) {
                const seriesKey = series.getSeriesKey();
                if (!this.mentionDataProvidersMap.get(seriesKey)) {
                    this.mentionDataMap.set(seriesKey, null);
                }
            }
        }

        // construct saved topic search object requests
        const savedMentionSearches: Observable<SavedMentionSearchApiModel>[] = [];
        for (const [series, search] of seriesSavedSearchRequestMap.entries()) {
            savedMentionSearches.push(
                search.pipe(
                    tap(response => this.setSeriesSearches(series, [response])),
                    tap(() => {
                        this.addToSearchMap(searchesMap, series.toSearchCriteriaModel(), series);
                    }),
                    catchError(error => {
                        if (error.status === 404) {
                            // Topic or subscription no longer exists
                            const seriesKey = series.getSeriesKey();
                            if (!this.mentionDataProvidersMap.get(seriesKey)) {
                                // The facetDataMap is empty for this series key, fetch the data for it
                                this.mentionDataMap.set(seriesKey, null);
                                this.mentionDataProvidersMap.set(seriesKey, new BehaviorSubject(null));
                            }

                            const errorString = this.localizationService.transform('Reports.error.topicRemoved');
                            this.mentionDataProvidersMap.get(seriesKey).error(new Error(errorString));
                            series.dataProvider.error(new Error(errorString));
                            this.checkIfShouldSetGlobalState();
                        }
                        return of(null);
                    })
                )
            );
        }

        // request saved search object
        forkJoin(savedMentionSearches).
            pipe(
                defaultIfEmpty(null),
                concatMap(async (_) => {
                    const facetSearches: Observable<MentionsSearchResponseModel>[] = [];

                    for (const [searchCriteria, seriesList] of searchesMap.entries()) {
                        const isFacetSearch = seriesList[0].chartType !== ReportBlockTypeEnum.TOP_MENTIONS;
                        const request = await this.prepareSearch(seriesList, searchCriteria, isFacetSearch);
                        if (request) {
                            facetSearches.push(request);
                        }
                    }

                    return forkJoin(facetSearches);
                }),
                concatMap(v => {
                    return v;
                })
            ).subscribe(() => {
                this.checkIfShouldSetGlobalState();
            });
    }

    /**
     * Maps the forkJoin of the two saved-search requests onto the series model
     */
    setSeriesSearches(series: ReportSeriesModel, responses: SavedMentionSearchApiModel[]): SavedMentionSearchApiModel[]  {
        for (const response of responses) {
            if (response.id === series.mentions.topicId) {
                series.mentions.topic = new MentionSearchModel(response);
            } else if (response.id === series.mentions.mediaSubscriptionId) {
                series.mentions.mediaSubscription = new MentionSearchModel(response);
            } else if (response.id === series.mentions.socialTopicId) {
                series.mentions.socialTopic = new MentionSearchModel(response);
            } else if (response.id === series.mentions.themeId) {
                series.mentions.theme = new MentionSearchModel(response);
            }

            if (response.id === series.mentions.searchCriteriaId) {
                series.mentions.filters = new MentionSearchModel(response);
            }
        }

        return responses;
    }

    addToSearchMap(map: Map<SearchCriteriaModel, ReportSeriesModel[]>, key: SearchCriteriaModel, value: ReportSeriesModel): void {
        if (!value.searchResults && value.cacheResult) {
            value.searchResults = new ReportSeriesSearchResult();
        }

        // Make the previous period series and add it to the map if needed
        if (value.hasPreviousPeriod) {
            const previousPeriodSeries: ReportSeriesModel = value.clone();
            previousPeriodSeries.hasPreviousPeriod = false;
            previousPeriodSeries.mentions.isPreviousPeriod = true;
            previousPeriodSeries.previousPeriodDataProvider = value.previousPeriodDataProvider;
            previousPeriodSeries.searchResults = value.searchResults;

            const previousPeriodKey: SearchCriteriaModel = JSON.parse(JSON.stringify(key));
            if (previousPeriodKey.query.harvestedDate) {
                previousPeriodKey.query.harvestedDate = DateQueryModel.getPreviousPeriodSearchCriteriaDateQueryModel(previousPeriodKey.query.harvestedDate);
            }

            if (previousPeriodKey.query.publicationDate) {
                previousPeriodKey.query.publicationDate = DateQueryModel.getPreviousPeriodSearchCriteriaDateQueryModel(previousPeriodKey.query.publicationDate);

            }

            this.addToSearchMap(map, previousPeriodKey, previousPeriodSeries);
        }
        // first we check for duplicate search models (to avoid making multiple equivalent requests)
        // however, we actually only compare a subset of the search. because if searches only differ in their facets,
        // we can merge the facets and reuse the result of that search for both (or more) charts.
        // unfortunately, it's not clear how (if it's feasible) to merge requests with different date facets
        for (const [searchCriteria, seriesList] of map.entries()) {
            if (
                JSON.stringify(searchCriteria.query) === JSON.stringify(key.query)
                && JSON.stringify(searchCriteria.filterQuery) === JSON.stringify(key.filterQuery)
                && this.savedFilterIdsMatch(searchCriteria.savedFilterIds, key.savedFilterIds)
                && this.dateFacetsAreMergeable(searchCriteria.dateFacets, key.dateFacets)
                && JSON.stringify(searchCriteria.prominenceByReachRequest) === JSON.stringify(key.prominenceByReachRequest)
                && !value.useMentionProvider && seriesList.filter(x => x.useMentionProvider).length === 0
                && searchCriteria.isWordCloudRequest === key.isWordCloudRequest
                && seriesList.every(x => x.timeInterval === value.timeInterval)
            ) {
                // if there's an equivalent query already in the map, merge the facets, append the series key, done
                // the Set constructor is used to remove duplicate entries.
                searchCriteria.facets = [ ...new Set([ ...(searchCriteria.facets ?? []), ...(key.facets ?? [])]) ];

                // also merge date facets
                if (searchCriteria.dateFacets?.length > 0) {
                    for (let i = 0; i < searchCriteria.dateFacets.length; i++) {
                        searchCriteria.dateFacets[i].prominenceGroupIds = [ ...new Set([
                            ...(searchCriteria.dateFacets[i].prominenceGroupIds ?? []),
                            ...(key.dateFacets[i]?.prominenceGroupIds ?? []),
                        ]) ];
                        searchCriteria.dateFacets[i].aggregateFacets = [ ...new Set([
                            ...(searchCriteria.dateFacets[i].aggregateFacets ?? []),
                            ...(key.dateFacets[i]?.aggregateFacets ?? [])
                        ]) ];
                    }
                } else {
                    searchCriteria.dateFacets = key.dateFacets;
                }

                // also merge the neofacets
                if (searchCriteria.neoFacets?.length > 0) {
                    for (let j = 0; j < value.mentions.neoFacets?.length; j++) {
                        const seriesNeoFacetRequest = value.mentions.neoFacets[j];
                        let alreadyFound = false;
                        for (let i = 0; i < searchCriteria.neoFacets.length; i++) {
                            const neoFacetRequest = searchCriteria.neoFacets[i];
                            if (neoFacetRequest.key === seriesNeoFacetRequest.key) {
                                alreadyFound = true;
                                break;
                            }
                        }
                        if (!alreadyFound) {
                            searchCriteria.neoFacets.push(seriesNeoFacetRequest);
                        }
                    }
                } else {
                    searchCriteria.neoFacets = value.mentions.neoFacets || [];
                }

                if (searchCriteria.neoFacets?.length > 0) {
                    for (let j = 0; j < key.neoFacets?.length; j++) {
                        const seriesNeoFacetRequest = key.neoFacets[j];
                        let alreadyFound = false;
                        for (let i = 0; i < searchCriteria.neoFacets.length; i++) {
                            const neoFacetRequest = searchCriteria.neoFacets[i];
                            if (neoFacetRequest.key === seriesNeoFacetRequest.key) {
                                alreadyFound = true;
                                break;
                            }
                        }
                        if (!alreadyFound) {
                            searchCriteria.neoFacets.push(seriesNeoFacetRequest);
                        }
                    }
                } else {
                    searchCriteria.neoFacets = key.neoFacets || [];
                }

                // having engagementBucketRequests part of the payload for saved mentions neo facets gives you a 500
                if (mentionSeriesTypes.includes(value.type) && value.isSavedSource()) {
                    searchCriteria.neoFacets?.map(neoFacet => {
                        delete neoFacet.engagementBucketRequests;
                    });
                }

                seriesList.push(value);
                return;
            }
        }
        map.set(key, [ value ]);
    }

    dateFacetsAreMergeable(a: DateFacetModel[], b: DateFacetModel[]): boolean {
        if (a.length === 0 || b.length === 0) {
            return true;
        }

        if (a.length !== b.length) {
            return false;
        }

        for (let i = 0; i < a.length; i++) {
            if (a[i].sliceSizeInSecond !== b[i].sliceSizeInSecond) {
                return false;
            }
        }

        return true;
    }

    savedFilterIdsMatch(a: number[], b: number[]) {
        if (!a && b || a && !b) {
            return false;
        }
        if (a === b) {
            return true;
        }
        if (a.length !== b.length) {
            return false;
        }
        if (a.sort().join(',') !== b.sort().join(',')) {
            return false;
        }

        return true;
    }


    /**
     * make search calls for each search criteria
     *
     * @param seriesList a list of report series that can use the same search criteria
     * @param searchCriteria the search content
     */
    async prepareSearch(
        seriesList: ReportSeriesModel[],
        searchCriteria: SearchCriteriaModel,
        isFacetSearch: boolean = true
    ): Promise<Observable<MentionsSearchResponseModel>> {
        if (!seriesList || seriesList.length === 0) {
            return;
        }

        // all series in the list should be equivalent (all able to re-use the results of the search), just use the first
        const series_ = seriesList[0];
        const seriesKey: string = series_.getSeriesKey();
        if (this.mentionDataProvidersMap.get(seriesKey)) {
            // we've already fetched the data for this series.
            return;
        }

        if (series_.prominenceTrendGroup) {
            searchCriteria.prominenceByReachRequest = new ProminenceByReachRequestModel(series_.prominenceTrendGroup.id);
        }

        if (series_.mentions?.filters?.searchCriteria?.query?.includedTweetTypes?.length > 0) {
            searchCriteria.query.tweetTypeFilter.include = series_.mentions.filters.searchCriteria.query.includedTweetTypes.map(
                x =>
                    TwitterSubTypeEnum[Object.entries(TwitterSubTypeEnum).find(([key, val]) => val === x.childId)[0]]
            );
        } else if (series_.mentions?.filters?.searchCriteria?.query?.excludedTweetTypes?.length > 0) {
            searchCriteria.query.tweetTypeFilter.exclude = series_.mentions.filters.searchCriteria.query.excludedTweetTypes.map(
                x =>
                    TwitterSubTypeEnum[Object.entries(TwitterSubTypeEnum).find(([key, val]) => val === x.childId)[0]]
            );
        }

        if (series_.mentions?.filters?.searchCriteria?.query?.includedLinkedinTypes?.length > 0) {
            searchCriteria.query.linkedinTypeFilter.include = series_.mentions.filters.searchCriteria.query.includedLinkedinTypes.map(
                x =>
                    LinkedinSubTypeEnum[Object.entries(LinkedinSubTypeEnum).find(([key, val]) => val === x.childId)[0]]
            );
        } else if (series_.mentions?.filters?.searchCriteria?.query?.excludedLinkedinTypes?.length > 0) {
            searchCriteria.query.linkedinTypeFilter.exclude = series_.mentions.filters.searchCriteria.query.excludedLinkedinTypes.map(
                x =>
                    LinkedinSubTypeEnum[Object.entries(LinkedinSubTypeEnum).find(([key, val]) => val === x.childId)[0]]
            );
        }

        if (series_.mentions?.filters?.searchCriteria?.query?.includedBlueskyTypes?.length > 0) {
            searchCriteria.query.blueskyTypeFilter.include = series_.mentions.filters.searchCriteria.query.includedBlueskyTypes.map(
                x =>
                    BlueskySubTypeEnum[Object.entries(BlueskySubTypeEnum).find(([key, val]) => val === x.childId)[0]]
            );
        } else if (series_.mentions?.filters?.searchCriteria?.query?.excludedBlueskyTypes?.length > 0) {
            searchCriteria.query.blueskyTypeFilter.exclude = series_.mentions.filters.searchCriteria.query.excludedBlueskyTypes.map(
                x =>
                    BlueskySubTypeEnum[Object.entries(BlueskySubTypeEnum).find(([key, val]) => val === x.childId)[0]]
            );
        }

        if (series_.mentions?.filters?.searchCriteria?.query?.includedTvTypes?.length > 0) {
            searchCriteria.query.tvTypeFilter.include = series_.mentions.filters.searchCriteria.query.includedTvTypes.map(
                x => x.childId as BroadcastSubTypeEnum
            );
        } else if (series_.mentions?.filters?.searchCriteria?.query?.excludedTvTypes?.length > 0) {
            searchCriteria.query.tvTypeFilter.exclude = series_.mentions.filters.searchCriteria.query.excludedTvTypes.map(
                x => x.childId as BroadcastSubTypeEnum
            );
        }

        if (searchCriteria.query.adValueUsd) {
            const range = searchCriteria.query.adValueUsd;
            searchCriteria.query.adValueUsd = {
                min: this.currencyConversionService.convertUserCurrencyToUSD(range.min),
                max: this.currencyConversionService.convertUserCurrencyToUSD(range.max)
            };
        }
        if (isFacetSearch) {
            const dateQuery = searchCriteria.query.harvestedDate || searchCriteria.query.publicationDate;
            // neofacets doesn't support 'all'
            if (dateQuery) {
                // Now that we have the dateQuery, we need to set up the neoFacets that have date requests with their proper range and gap values
                for (const neoFacetRequest of searchCriteria.neoFacets) {
                    if (!neoFacetRequest.dateField) {
                        continue;
                    }

                    let startISOTimeString: string = '';
                    let endISOTimeString: string = '';
                    if (dateQuery?.dateType === DateQueryTypeEnum.RELATIVE) {
                        startISOTimeString = moment().add(dateQuery.relativeStartOffsetH, 'hours').toISOString();
                        endISOTimeString = moment().add(dateQuery.relativeEndOffsetH, 'hours').toISOString();
                    } else if (dateQuery?.dateType === DateQueryTypeEnum.ABSOLUTE) {
                        startISOTimeString = moment(dateQuery.dateRange.start).toISOString();
                        endISOTimeString = moment(dateQuery.dateRange.end).toISOString();
                    } else if (dateQuery?.dateType === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
                        startISOTimeString = DateQueryModel.getStartDateForRelativeTimezone(
                            dateQuery.relativeTimezoneDateType, dateQuery.timeZone
                        ).toISOString();
                        endISOTimeString = DateQueryModel.getEndDateForRelativeTimezone(dateQuery.relativeTimezoneDateType, dateQuery.timeZone).toISOString();
                    } else {
                        // doesn't reach here now that we added the if statement..
                        // throw Error('Can\'t generate date neo facet for all');
                    }

                    neoFacetRequest.range = {
                        start: startISOTimeString,
                        end: endISOTimeString
                    };

                    if (!series_.timeInterval || series_.timeInterval === ChartTimeIntervalsEnum.DYNAMIC) {
                        if (DateQueryModel.getTotalHoursInApiQuery(dateQuery) > 24
                            && dateQuery.relativeTimezoneDateType !== RelativeTimezoneDateTypeEnum.LAST_24_HOURS
                        ) {
                            neoFacetRequest.gap = 86400000; // 24 hours * 60 minutes * 60 seconds * 1000 milliseconds
                        } else {
                            neoFacetRequest.gap = 3600000; // 60 minutes * 60 seconds * 1000 milliseconds
                        }
                    } else {
                        switch (series_.timeInterval) {
                            case ChartTimeIntervalsEnum.WEEK:
                                neoFacetRequest.gap = 86400000 * 7;
                                break;
                            case ChartTimeIntervalsEnum.MONTH:
                                neoFacetRequest.gap = 86400000 * 30;
                                break;
                            case ChartTimeIntervalsEnum.QUARTER:
                                neoFacetRequest.gap = 86400000 * 90;
                                break;
                            case ChartTimeIntervalsEnum.YEAR:
                                neoFacetRequest.gap = 31556926000;
                                break;
                        }
                    }
                }
            }

            // this will ask backend to return combinedQuery
            searchCriteria.requireCombinedQueryResults = true;
        } else {
            searchCriteria.facets = [];
            searchCriteria.dateFacets = [];
            searchCriteria.sortField = series_.sortField;
            searchCriteria.page = 1;
            searchCriteria.pageSize = 25;
        }

        let search: Observable<MentionsSearchResponseModel>;
        // Check the cache, if it has it, use the cache, if not, make a live call
        const cachedValue: ReportsServiceCacheModel = await this.indexedDbCoreCacheService.get(seriesKey);
        const cachedPeriodValue: MentionsSearchResponseModel = series_.mentions.isPreviousPeriod ? cachedValue?.previousPeriod : cachedValue?.currentPeriod;
        let useCache: boolean = !!cachedValue && !!cachedPeriodValue;

        if (useCache && isFacetSearch) {
            // Check to see if the cached value has all the data needed, if it doesn't have all the data needed
            // Modify the request, so it only asks for what's needed
            const missingNeoFacets: NeoFacetRequestApiModel[] = differenceBy(searchCriteria.neoFacets, cachedValue.facets.neoFacets, 'key');

            // a neoFacet can match by key but still be stale if its date-bucket gap changed (e.g. the report's time interval changed)
            const staleGapNeoFacets: NeoFacetRequestApiModel[] = searchCriteria.neoFacets.filter(requested => {
                const cachedMatch = cachedValue.facets?.neoFacets?.find(cached => cached.key === requested.key);
                return cachedMatch && requested.gap !== cachedMatch.gap;
            });

            if (missingNeoFacets.length || staleGapNeoFacets.length) {
                searchCriteria.neoFacets = [ ...missingNeoFacets, ...staleGapNeoFacets ];
                useCache = false;
            }

            const missingDateFacet: DateFacetModel[] = differenceBy(searchCriteria.dateFacets, cachedValue.facets.dateFacets, 'sliceSizeInSecond');
            if (missingDateFacet.length) {
                searchCriteria.dateFacets = missingDateFacet;
                useCache = false;
            }

            const missingFacets: MentionSearchFacetTypesEnum[] = difference(searchCriteria.facets, cachedValue.facets.facets);
            if (missingFacets.length) {
                searchCriteria.facets = missingFacets;
                useCache = false;
            }
        }

        if (searchCriteria.query.publicationDate && !searchCriteria.query.publicationDate.timeZone) {
            searchCriteria.query.publicationDate.timeZone = this.userTimezonePreference;
        }
        if (searchCriteria.query.harvestedDate && !searchCriteria.query.harvestedDate.timeZone) {
            searchCriteria.query.harvestedDate.timeZone = this.userTimezonePreference;
        }

        if (useCache) {
            search = of(cachedPeriodValue);
        } else if (series_.isUnsavedSource()) {
            search = this.mentionsApi.searchMentions(searchCriteria);
        } else if (series_.isSavedSource()) {
            search = this.mentionsApi.searchAccount(searchCriteria);
        } else {
            console.error('bad data: ', series_);
            this.hasBadData = true;
            this.mentionDataProvidersMap.get(seriesKey)?.error(new Error('No data source selected'));
            this.checkIfShouldSetGlobalState();

            if (this.isRefresh) {
                this.isRefresh = false;
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Reports.refreshFailureMessage.summary'),
                    detail: this.localizationService.transform(
                        'Reports.refreshFailureMessage.detail',
                        { reportName: this.currentReport.name}
                    )
                });
            }

            return;
        }

        const errorHandlerFunction: Function = this.getErrorHandlerFunction(series_, seriesKey);

        return search?.pipe(
            catchError(error => {
                this.isRefresh = false;
                // we need to add the loop to set the errors on ALL the charts for that type, not only "const series_ = seriesList[0];" from above
                // since we only make 1 call per series type.. only 1 chart would show the error message, the rest would show the spinning
                for (let i = 0; i < seriesList.length; i++) {
                    errorHandlerFunction(error, seriesList[i]);
                }
                return of(errorHandlerFunction(error));
            }),
            tap((content: MentionsSearchResponseModel) => {
                if (!content) {
                    this.checkIfShouldSetGlobalState();
                    return;
                }

                // we need to do this, because the topic does not have any social media types defined...
                // and because of that, when filerting the mediaTypeFilter on the topic with the social networks defined, we end up sending it as as empty...
                // which means get ALL mediaTypes & ends up displaying anything, which is not accurate...
                // in this scenario for TOP_POSTS if mediaTypeFilter is empty, we don't want to show any mentions
                if (series_._chartType === ReportBlockTypeEnum.TOP_POSTS && series_.isUnsavedSource()) {
                    if (!searchCriteria?.query?.mediaTypeFilter?.include?.length) {
                        content.mentions = [];
                    }
                }

                // Downstream facet mapping should evaluate against the backend's combined query
                // (source search + filters merged). Build it on a copy — mutating the live request
                // object corrupts the body of a pending rate-limit retry of this request (query
                // became undefined when the response carried no combinedQuery, and the retry was
                // then rejected with a 400 "query must not be null").
                const effectiveSearchCriteria: SearchCriteriaModel = { ...searchCriteria };
                effectiveSearchCriteria.query = content.combinedQuery ?? searchCriteria.query;
                effectiveSearchCriteria.filterQuery = null;

                // Cache the new value we just got
                this.indexedDbCoreCacheService.get(seriesKey).then(
                    (value: ReportsServiceCacheModel) => {
                        const facetsToCache: ReportsServiceCacheModel = this.getMergedCachedFacets(
                            value || { previousPeriod: null, currentPeriod: null, facets: {} },
                            effectiveSearchCriteria,
                            content,
                            series_.mentions.isPreviousPeriod
                        );
                        this.indexedDbCoreCacheService.set(seriesKey, facetsToCache);

                        for (let i = 0; i < seriesList.length; i++) {
                            // the response contains a combined query of the original topic search object and the filters saved on each report source
                            // will use combinedQuery to create urls for each data point
                            seriesList[i].mentions.combinedQuery = new MentionSearchQueryModel(content.combinedQuery);

                            if (seriesList[i].cacheResult) {
                                if (seriesList[i].mentions.isPreviousPeriod) {
                                    seriesList[i].searchResults.previousPeriodResults = facetsToCache.previousPeriod;
                                } else {
                                    seriesList[i].searchResults.currentPeriodResults = facetsToCache.currentPeriod;
                                }
                            }

                            if (seriesList[i].useMentionProvider || !isFacetSearch) {
                                this.mapMentionResults(seriesList[i], content);
                            } else {
                                this.mapMentionFacetResults(
                                    seriesList[i],
                                    effectiveSearchCriteria,
                                    series_.mentions.isPreviousPeriod ? facetsToCache.previousPeriod : facetsToCache.currentPeriod
                                );
                            }
                        }
                        this.checkIfShouldSetGlobalState();
                    }
                );
            })
        );
    }

    getErrorHandlerFunction(series: ReportSeriesModel, seriesKey: string): Function {
        switch (series.type) {
            case SeriesTypeEnum.folder:
            case SeriesTypeEnum.tag:
            case SeriesTypeEnum.tagGroup:
            case SeriesTypeEnum.sourceGroup:
            case SeriesTypeEnum.aiAgent:
                return (error, returnedSeries) => {
                    let isNotFoundError = false;
                    if (!returnedSeries) {
                        returnedSeries = series;
                    }

                    if (error && error.error && error.error.details) {
                        for (const errorMessage of error.error.details) {
                            if (errorMessage.code === CommonApiErrorEnum.badReference) {
                                isNotFoundError = true;
                            }
                        }
                    }

                    if (isNotFoundError) {
                        const returnedSeriesKey = returnedSeries.getSeriesKey();
                        const errorString = this.localizationService.transform(`Reports.error.${returnedSeries.type}Removed`);

                        if (!this.mentionDataProvidersMap.get(returnedSeriesKey)) {
                            this.mentionDataMap.set(returnedSeriesKey, null);
                            this.mentionDataProvidersMap.set(returnedSeriesKey, new BehaviorSubject(null));
                        }

                        this.mentionDataProvidersMap.get(returnedSeriesKey).error(new Error(errorString));
                        returnedSeries.dataProvider.error(new Error(errorString));
                        this.checkIfShouldSetGlobalState();
                    }
                };
                break;
            case SeriesTypeEnum.topic:
            case SeriesTypeEnum.subscription:
            case SeriesTypeEnum.socialTopic:
                return (error, returnedSeries) => {
                    if (!returnedSeries) {
                        returnedSeries = series;
                    }
                    if (error.status === HttpStatusCode.BadRequest) {
                        // B/E would return 400 if the date range is out of the B/E scope, so we just assume the data is empty
                        // We'll need to discuss with B.E and product owner on how to proper flag and display data error related message
                        return new MentionsSearchResponseModel();
                    } else {
                        const returnedSeriesKey = returnedSeries.getSeriesKey();
                        if (!this.mentionDataProvidersMap.get(returnedSeriesKey)) {
                            this.mentionDataMap.set(returnedSeriesKey, null);
                            this.mentionDataProvidersMap.set(returnedSeriesKey, new BehaviorSubject(null));
                        }
                        this.mentionDataProvidersMap.get(returnedSeriesKey)?.error(error);
                    }
                };
                break;
            case SeriesTypeEnum.theme:
                if  (this.mentionDataProvidersMap.get(seriesKey)?.error) {
                    return (error) => this.mentionDataProvidersMap.get(seriesKey)?.error(error);
                } else {
                    return (error) => {
                        if (error.status === HttpStatusCode.NotFound || error.status === HttpStatusCode.BadRequest ) {
                            return new MentionsSearchResponseModel();
                        } else {
                            return;
                        }
                    };
                }
                break;

            default:
                console.error('invalid series type: ', series.type);
                break;
        }
    }

    async mapMentionFacetResults(series: ReportSeriesModel, searchCriteria: SearchCriteriaModel, response: MentionsSearchResponseModel) {
        const seriesKey: string = series.getSeriesKey();
        this.mentionDataMap.set(seriesKey, response);
        this.mentionDataProvidersMap.set(seriesKey, new BehaviorSubject(response));
        if (!response) {
            this.mentionDataProvidersMap.get(seriesKey).error(new Error());
            series.dataProvider.error(new Error(this.localizationService.transform('Common.noContentToDisplay')));
            return;
        }

        if (series.isValid()) {
            const response_ = this.setMentionZeroFacets(response, searchCriteria);
            // transform response into chart compatible input object
            let dataProcessingWorkflow: Observable<DataPointModel[]>;
            if (newDataProcessingWorkflowBlockTypes.includes(series.chartType)) {
                dataProcessingWorkflow = this.reportsSeriesDataMapperService.mapFacetValues(response_, series);
            } else {
                dataProcessingWorkflow = this.reportsSeriesMapperService.mapFacetValues(series, response_);
            }
            dataProcessingWorkflow.subscribe({
                next: (data) => {
                    if (series.mentions.isPreviousPeriod) {
                        series.previousPeriodDataProvider.next(data);
                    } else {
                        // emit new data to charts
                        series.dataProvider.next(data);
                    }
                },
                error: (error) => {
                    // If we don't add an error case it shows a red error message in console
                    series.dataProvider.error(error);
                }
            });
        }
    }

    /**
     * Modifies the passed in response model to have data in the form downsteam services/components expect
     */
    setMentionZeroFacets(response: MentionsSearchResponseModel, request: SearchCriteriaModel): MentionsSearchResponseModel {
        // Fix date facets
        const requestStart = this.getMentionSearchRequestStart(request);
        const requestEnd = this.getMentionSearchRequestEnd(request);

        response?.dateFacetResults?.forEach(
            (dateFacets, index) => {
                // Filter out the values that don't start and end before the start and end specified in the request
                if (requestStart && requestEnd) {
                    dateFacets.values = dateFacets.values.filter(
                        (facetValue) => {
                            return facetValue.from >= +requestStart && facetValue.from <= +requestEnd;
                        }
                    );
                } else {
                    // All case, have to find the first non-zero and trim from there
                    for (let i = 0; i < dateFacets.values.length; i++) {
                        if (dateFacets.values[i].count) {
                            dateFacets.values = dateFacets.values.slice(i);
                            break;
                        }
                    }

                    // Trim the end to the first non-zero
                    for (let i = dateFacets.values.length - 1; i >= 0; i--) {
                        if (dateFacets.values[i].count) {
                            dateFacets.values = dateFacets.values.slice(0, i + 1);
                            break;
                        }
                    }
                }

                // Sets aggregateFacets on every node that they aren't set
                const requestedAggregateFacets = request.dateFacets[index]?.aggregateFacets;
                if (requestedAggregateFacets) {
                    dateFacets.values.map(
                        facetValue => {
                            requestedAggregateFacets.forEach(
                                requestedAggregateFacet => {
                                    if (facetValue.aggregateFacets[requestedAggregateFacet] === undefined) {
                                        facetValue.aggregateFacets[requestedAggregateFacet] = 0;
                                    }
                                }
                            );
                        }
                    );
                }
            }
        );

        // Fix non-date facets
        for (const facetType of (response?.facetResults || [])) {
            switch (facetType.field) {
                case MentionSearchFacetTypesEnum.mediaType:
                    for (const requestedMediaType of request.query.mediaTypeFilter.include) {
                        let found = false;

                        for (const facetValue of facetType.values) {
                            if (facetValue.value === requestedMediaType) {
                                found = true;
                                break;
                            }
                        }

                        if (!found) {
                            facetType.values.push({
                                count: 0,
                                value: requestedMediaType
                            });
                        }
                    }

                    break;
                case MentionSearchFacetTypesEnum.tweetType:
                    for (const requestedTweetType of request.query.tweetTypeFilter.include) {
                        const found = facetType.values?.some(facet => facet.value === requestedTweetType) || false;

                        if (!found) {
                            facetType.values.push({
                                count: 0,
                                value: requestedTweetType
                            });
                        }
                    }
                    break;
                case MentionSearchFacetTypesEnum.linkedinType:
                    for (const requestLinkedInType of request.query.linkedinTypeFilter.include) {
                        const found = facetType.values?.some(facet => facet.value === requestLinkedInType) || false;

                        if (!found) {
                            facetType.values.push({
                                count: 0,
                                value: requestLinkedInType
                            });
                        }
                    }
                    break;
                case MentionSearchFacetTypesEnum.blueskyType:
                    for (const requestBlueskyType of request.query.blueskyTypeFilter.include) {
                        const found = facetType.values?.some(facet => facet.value === requestBlueskyType) || false;

                        if (!found) {
                            facetType.values.push({
                                count: 0,
                                value: requestBlueskyType
                            });
                        }
                    }
                    break;
            }
        }

        return response;
    }

    mapMentionResults(series: ReportSeriesModel, response: MentionsSearchResponseModel): void {
        const seriesKey = series.getSeriesKey();
        this.mentionDataMap.set(seriesKey, response);
        this.mentionDataProvidersMap.set(seriesKey, new BehaviorSubject(response));
        const mentions = response.mentions.map(x => new Mention(x));
        series.mentionProvider.next(mentions);
    }

    public async prepareExternalSeries() {
        if (!this.googleAnalyticsDataConnectors) {
            // Wait for the connectors to come in.
            // The subscription will re-trigger this call when they're available
            return;
        }

        this.externalDataMap.clear();
        this.externalDataProvidersMap.clear();

        // check that the external sources are still valid
        for (const series of this.currentReport.getExternalDataSeries()) {
            //  we don't want to make api calls for shared report ones
            if (series.externalData.externalDataType === DataConnectorTypesEnum.GOOGLE_ANALYTICS
                || series.externalData.externalDataType === DataConnectorTypesEnum.GOOGLE_ANALYTICS_4 && !series.isSharedReport) {
                if (!this.validateGoogleAnalyticsSeries(series)) {
                    const seriesKey = series.getSeriesKey();
                    if (!this.externalDataProvidersMap.get(seriesKey)) {
                        // The facetDataMap is empty for this series key, fetch the data for it
                        this.mentionDataMap.set(seriesKey, null);
                        this.externalDataProvidersMap.set(seriesKey, new BehaviorSubject(null));
                    }

                    const errorString = this.localizationService.transform('Reports.error.externalDataSourceRemoved');
                    this.externalDataProvidersMap.get(seriesKey).error(new Error(errorString));
                    series.mentions.topicId = null;
                    series.dataProvider.error(new Error(errorString));
                    this.hasBadData = true;
                    this.checkIfShouldSetGlobalState();
                    continue;
                }
                await this.handleExternalSeriesBackwardCompatibility(series);

                this.loadDataForExternalGoogleAnalyticsSeries(series);
            }
        }
    }

    public validateGoogleAnalyticsSeries(series: ReportSeriesModel): boolean {
        return !!this.googleAnalyticsDataConnectors.find(connector => {
            if (connector.id !== series.externalData.dataConnectorId) {
                return false;
            }

            if (series.externalData.externalDataType === DataConnectorTypesEnum.GOOGLE_ANALYTICS) {
                return !!connector.googleAnalyticsAccounts.find(acct => {
                    return !!acct.profiles?.find(profile => profile.id === series.externalData.externalDataId);
                });
            } else {
                // GA4 accounts do not have profiles
                return true;
            }
        });
    }

    public async handleExternalSeriesBackwardCompatibility(series: ReportSeriesModel): Promise<void> {
        if (series.dateRange.relativeTimezoneDateType === RelativeTimezoneDateTypeEnum.LAST_24_HOURS
            && !series.externalData.timeZone
        ) {
            const correspondingBlock = this.currentReport.blocks.find(x => (x.displayElement as ChartDisplayElementModel).series.some(y => series.isEqual(y)));
            if (correspondingBlock) {
                series.dateRange = (correspondingBlock.displayElement as ChartDisplayElementModel).getMentionSeries()[0]?.dateRange;
            }
            if (!series.externalData.timeZone) {
                const properties = await new Promise((resolve) => {
                    this.dataConnectorsApiService.getGoogleAnalyticsProperties(
                        series.externalData.dataConnectorId,
                        series.externalData.googleAnalyticsAccountId
                    ).subscribe({
                        next: x => resolve(x),
                        error: err => resolve(null)
                    });
                });
                if (properties) {
                    series.externalData.timeZone = properties[0]?.timeZone;
                }
            }
        }
    }

    public async loadDataForExternalGoogleAnalyticsSeries(series: ReportSeriesModel): Promise<void> {
        const seriesKey: string = series.getSeriesKey();

        if (!this.externalDataMap.get(seriesKey)) {
            this.externalDataMap.set(seriesKey, true);
            this.externalDataProvidersMap.set(seriesKey, new BehaviorSubject(null));
            const hrs = DateQueryModel.getTotalHoursInQuery(series.dateRange);
            const frequency = hrs > 24 ? GoogleAnalyticsReportFrequencyEnum.DAILY : GoogleAnalyticsReportFrequencyEnum.HOURLY;

            const dateFilter = series.dateRange.toSearchQueryApiModel();
            if (dateFilter.dateType === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
                dateFilter.timeZone = this.userTimezonePreference;
            }

            this.externalDataApi.getGoogleAnalyticsData(
                series.externalData.dataConnectorId,
                series.externalData.externalDataId,
                {
                    dateFilter,
                    reportType: series.externalData.googleAnalyticsReportType,
                    timePeriod: frequency,
                    gaRequestTimeZone: series.externalData.timeZone
                }
            ).subscribe({
                next: (data) => {
                    this.externalDataMap.set(seriesKey, data);
                    this.externalDataProvidersMap.get(seriesKey).next(data);
                    this.checkIfShouldSetGlobalState();
                },
                error: (error) => {
                    this.externalDataProvidersMap.get(seriesKey).error(error);
                    this.checkIfShouldSetGlobalState();
                }
            });
        }
        this.checkIfShouldSetGlobalState();

        if (series.isValid()) {
            this.externalDataProvidersMap.get(seriesKey).subscribe({
                next: (response: GoogleAnalyticsReportResponseModel) => {
                    if (!response) {
                        // Data hasn't been fetched yet
                        return;
                    }

                    this.externalDataMapperService.mapGoogleAnalytics(series, response)
                        .subscribe({
                            next: (data) => {
                                series.dataProvider.next(data);
                            },
                            error: (error) => {
                                // If we don't add an error case it shows a red error message in console
                                series.dataProvider.error(error);
                            }
                        });
                },
                error: (error) => {
                    series.dataProvider.error(error);
                }
            });
        }
    }

    /**
     * Hard refresh, requests any topic that was loaded as part of the load and requests all facet data again
     */
    public async refreshAll(isRefresh: boolean = true): Promise<void> {
        this.isRefresh = isRefresh;
        await this.resetReportSeries();
        this.prepareReport();
    }

    public async resetReportSeries(): Promise<void> {
        this.mentionDataMap.clear();
        this.mentionDataProvidersMap.clear();
        this.externalDataMap.clear();
        this.externalDataProvidersMap.clear();
        for (const series of this.currentReport.getSeries()) {
            series.mentions.topic = null;
            series.mentions.mediaSubscription = null;
            series.mentions.socialTopic = null;
            series.mentions.theme = null;
            await this.indexedDbCoreCacheService.del(series.getSeriesKey());
        }
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    public setGlobalDateRange(date: ReportDateRangeModel, selectedDateOutsideRange: boolean = false) {
        this.currentReport.setGlobalDateRange(date, selectedDateOutsideRange);
    }

    public setGlobalMentionsSingleSeries(series: ReportSeriesModel[], updateAll: boolean): void {
        this.currentReport.setGlobalMentionSingleSeries(series, updateAll);
    }

    public setGlobalMentionsMultiSeries(series: ReportSeriesModel[], updateAll: boolean): void {
        this.currentReport.setGlobalMentionMultiSeries(series, updateAll);
    }

    public checkIfShouldSetGlobalState(): void {
        if (this.hasBadData) {
            this.setGlobalState(SeriesStateEnum.error);
            return;
        }

        if (this.mentionDataMap.size === 0 && this.externalDataMap.size === 0) {
            this.setGlobalState(SeriesStateEnum.none);
            return;
        }

        let state = SeriesStateEnum.loaded;
        const dataProviders: BehaviorSubject<MentionsSearchResponseModel | ExternalDataResponseModels>[] = [
            ...this.mentionDataProvidersMap.values(),
            ...this.externalDataProvidersMap.values()
        ];
        dataProviders.forEach(subject => {
            if (subject.hasError) {
                state = SeriesStateEnum.error;
                return;
            }
            if (!subject.getValue()) {
                state = SeriesStateEnum.loading;
            }
        });

        // if we're in the process of refreshing, and have loaded all our data, we should push a growl message
        // to the message service to notify the user the refresh has completed
        if (this.isRefresh && state === SeriesStateEnum.loaded) {
            this.isRefresh = false;
            this.messageService.add({
                severity: messageSeverityEnum.success,
                summary: this.localizationService.transform('Reports.refreshSuccessfulMessage.summary'),
                detail: this.localizationService.transform(
                    'Reports.refreshSuccessfulMessage.detail',
                    { reportName: this.currentReport.name}
                )
            });
        }

        this.setGlobalState(state);
    }

    public getGlobalState(): Observable<SeriesStateEnum> {
        return this.globalState$;
    }

    private setGlobalState(state: SeriesStateEnum): void {
        if (state === this.globalState) {
            return;
        }

        this.globalState = state;
        this.globalState$.next(state);
    }

    /**
     * Returns the start time for the request (regardless of if it's a relative or absolute date)
     */
    getMentionSearchRequestStart(request: SearchCriteriaModel): Date {
        if (request.query) {
            const dateQuery: SearchCriteriaDateQueryModel = request.query.harvestedDate || request.query.publicationDate || null;

            if (dateQuery) {
                if (dateQuery.dateType === DateQueryTypeEnum.ABSOLUTE) {
                    const start = moment(dateQuery.dateRange.start).startOf('second').valueOf();
                    return new Date(start);
                } else if (dateQuery.dateType === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
                    return DateQueryModel.getStartDateForRelativeTimezone(dateQuery.relativeTimezoneDateType, dateQuery.timeZone).toDate();
                } else {
                    return moment(new Date()).add(dateQuery.relativeStartOffsetH, 'hours').toDate();
                }
            }
        }

        return null;
    }

    /**
     * Returns the end time for the request (regardless of if it's a relative or absolute date)
     */
    getMentionSearchRequestEnd(request: SearchCriteriaModel): Date {
        if (request.query) {
            const dateQuery: SearchCriteriaDateQueryModel = request.query.harvestedDate || request.query.publicationDate || null;

            if (dateQuery) {
                if (dateQuery.dateType === DateQueryTypeEnum.ABSOLUTE) {
                    return new Date(dateQuery.dateRange.end);
                } else if (dateQuery.dateType === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
                    return DateQueryModel.getEndDateForRelativeTimezone(dateQuery.relativeTimezoneDateType, dateQuery.timeZone).toDate();
                } else {
                    return moment(new Date()).add(dateQuery.relativeEndOffsetH, 'hours').toDate();
                }
            }
        }

        return null;
    }

    clearMapping() {
        this.mentionDataProvidersMap.clear();
        this.mentionDataMap.clear();
        this.hasBadData = false;
    }

    /**
     * Takes the currently cached facets, the newly requested facet lists, the response and if the response is previous or not,
     * and updates the cached value with the results from the latest request
     */
    getMergedCachedFacets(
        currentCache: ReportsServiceCacheModel,
        newRequestedFacets: SearchCriteriaModel,
        response: MentionsSearchResponseModel,
        isPrevious: boolean
    ): ReportsServiceCacheModel {
        if (!response) {
            return currentCache;
        }

        // Check to see if we asked for new date facets
        if (newRequestedFacets.dateFacets?.length) {
            // handle date facets
            currentCache.facets.dateFacets = newRequestedFacets.dateFacets;
            if (isPrevious) {
                if (currentCache.previousPeriod) {
                    currentCache.previousPeriod.dateFacetResults = response.dateFacetResults;
                } else {
                    currentCache.previousPeriod = response;
                }
            } else {
                if (currentCache.currentPeriod) {
                    currentCache.currentPeriod.dateFacetResults = response.dateFacetResults;
                } else {
                    currentCache.currentPeriod = response;
                }
            }
        }

        // handle neo facets
        if (newRequestedFacets.neoFacets?.length) {
            currentCache.facets.neoFacets = unionBy(newRequestedFacets.neoFacets, currentCache.facets.neoFacets, 'key');

            if (isPrevious) {
                if (currentCache.previousPeriod) {
                    // Merge the two sets of neo-facets, remove duplicates
                    currentCache.previousPeriod.neoFacetResults = unionBy(response.neoFacetResults, currentCache.previousPeriod.neoFacetResults, 'key');
                } else {
                    currentCache.previousPeriod = response;
                }
            } else {
                if (currentCache.currentPeriod) {
                    currentCache.currentPeriod.neoFacetResults = unionBy(response.neoFacetResults, currentCache.currentPeriod.neoFacetResults, 'key');
                } else {
                    currentCache.currentPeriod = response;
                }
            }
        }

        // handle facets
        if (newRequestedFacets.facets?.length) {
            currentCache.facets.facets = union(newRequestedFacets.facets, currentCache.facets.facets);

            if (isPrevious) {
                if (currentCache.previousPeriod) {
                    // Merge the two sets of neo-facets, remove duplicates
                    currentCache.previousPeriod.facetResults = unionBy(response.facetResults, currentCache.previousPeriod.facetResults, 'field');
                } else {
                    currentCache.previousPeriod = response;
                }
            } else {
                if (currentCache.currentPeriod) {
                    currentCache.currentPeriod.facetResults = unionBy(response.facetResults, currentCache.currentPeriod.facetResults, 'field');
                } else {
                    currentCache.currentPeriod = response;
                }
            }
        }

        if ((newRequestedFacets.pageSize > 1)) {
            let key = 'id';
            if (response?.mentions?.length) {
                if (response?.mentions[0].hasOwnProperty('mercuryId')) {
                    key = 'mercuryId';
                }
            }
            if (isPrevious) {
                if (currentCache.previousPeriod) {
                    // Merge the two sets of neo-facets, remove duplicates
                    currentCache.previousPeriod.mentions = unionBy(response.mentions, currentCache.previousPeriod.mentions, key);
                } else {
                    currentCache.previousPeriod = response;
                }
            } else {
                if (currentCache.currentPeriod) {
                    currentCache.currentPeriod.mentions = unionBy(response.mentions, currentCache.currentPeriod.mentions, key);
                } else {
                    currentCache.currentPeriod = response;
                }
            }
        }

        return currentCache;
    }
}

interface ReportsServiceCacheModel {
    facets?: {
        dateFacets?: DateFacetModel[];
        facets?: MentionSearchFacetTypesEnum[];
        neoFacets?: NeoFacetRequestApiModel[];
    };
    previousPeriod?: MentionsSearchResponseModel;
    currentPeriod?: MentionsSearchResponseModel;
}
